/**
 * Tasting booking dialog. The form is a plain cart form for the deposit product (quantity = people,
 * date/name/phone as line item properties) that Shopify sends straight to checkout via return_to.
 * This only fills it from the chosen ticket, builds the next 10 days as radio chips (weekends only
 * for groups of 10+), keeps the people count in range and shows deposit, total and balance.
 */
class BookingDialog extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.form = this.querySelector('[data-booking-form]');
    this.people = this.querySelector('[data-booking-people]');
    this.price = 0;
    this.deposit = 0;

    const section = this.closest('section') || document;
    section.addEventListener('click', (event) => {
      const opener = event.target.closest('[data-booking-open]');
      if (opener) this.open(opener.dataset);
    });

    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog || event.target.closest('[data-booking-close]')) this.dialog.close();
    });

    if (!this.form) return;

    const first = section.querySelector('[data-booking-open]');
    if (first) this.read(first.dataset);

    this.querySelectorAll('[data-booking-step]').forEach((button) => {
      button.addEventListener('click', () => {
        this.people.value = this.clamp(Number(this.people.value) + Number(button.dataset.bookingStep));
        this.update();
      });
    });
    this.people.addEventListener('change', () => {
      this.people.value = this.clamp(Number(this.people.value));
      this.update();
    });
    this.renderDays();
    this.update();
  }

  clamp(value) {
    return Math.min(80, Math.max(1, Math.round(value) || 1));
  }

  /** Cents in the store's money format, e.g. "€0,00" → "€1.234,50". */
  money(cents) {
    const [int, dec] = (Math.round(cents) / 100).toFixed(2).split('.');
    const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return this.dataset.moneyFormat.replace(/0[,.]00/, `${grouped},${dec}`);
  }

  read({ name, price, place, variant, deposit }) {
    this.name = name;
    this.place = place || '';
    this.price = Math.round((Number(price) || 0) * 100);
    this.deposit = Number(deposit) || 0;
    // A ticket without its deposit variant (renamed ticket, missing variant) must never charge another ticket's deposit.
    this.querySelector('[data-booking-variant]').value = variant || '';
    this.querySelector('[type="submit"]').disabled = !variant;
    this.querySelector('[data-booking-missing]').hidden = Boolean(variant);
  }

  open(data) {
    if (this.form) {
      this.read(data);
      this.querySelector('[data-booking-title]').textContent = `${this.name} · ${this.money(this.price).replace(/[,.]00(?!\d)/, '')}`;
      this.querySelector('[data-booking-where]').textContent = this.place ? this.dataset.where.replace('%place%', this.place) : '';
      this.querySelector('[data-booking-field="place"]').value = this.place;
      this.update();
    }
    this.dialog.showModal();
  }

  renderDays() {
    const box = this.querySelector('[data-booking-days]');
    if (!box) return;
    const weekdays = this.dataset.weekdays.split(',');
    const months = this.dataset.months.split(',');
    const today = new Date();
    today.setHours(12, 0, 0, 0);

    for (let offset = 1; offset <= 10; offset += 1) {
      const day = new Date(today);
      day.setDate(today.getDate() + offset);
      const weekend = day.getDay() === 0 || day.getDay() === 6;

      const chip = document.createElement('label');
      chip.className = 'booking-day';
      chip.dataset.weekend = String(weekend);
      chip.innerHTML = '<input type="radio" required><span class="booking-day__wd"></span><span class="booking-day__n"></span>';
      const input = chip.querySelector('input');
      input.name = box.dataset.fieldName;
      input.value = `${weekdays[day.getDay()]} ${day.getDate()} ${months[day.getMonth()]} ${day.getFullYear()}`;
      chip.querySelector('.booking-day__wd').textContent = weekdays[day.getDay()];
      chip.querySelector('.booking-day__n').textContent = day.getDate();
      box.append(chip);
    }
    box.addEventListener('change', () => this.update());
  }

  update() {
    const count = this.clamp(Number(this.people.value));
    const group = count >= 10;

    this.querySelectorAll('[data-weekend="true"] input').forEach((input) => {
      input.disabled = !group;
      if (!group) input.checked = false;
    });
    this.querySelector('[data-booking-weekend]').hidden = group;

    const label = count === 1 ? this.dataset.person : this.dataset.persons;
    this.querySelector('[data-booking-people-label]').textContent = label;

    const chosen = this.querySelector('[data-booking-days] input:checked');
    const total = this.price * count;
    const deposit = this.deposit * count;
    this.querySelector('[data-booking-summary]').textContent = `${count} ${label} · ${chosen ? chosen.value : this.dataset.chooseDate}`;
    this.querySelector('[data-booking-amount]').textContent = this.money(deposit);
    this.querySelector('[data-booking-balance]').textContent = this.dataset.totalLine
      .replace('%total%', this.money(total))
      .replace('%balance%', this.money(Math.max(0, total - deposit)));
  }
}

if (!customElements.get('booking-dialog')) customElements.define('booking-dialog', BookingDialog);
